import { action, computed, observable } from 'mobx'
import { observer } from 'mobx-react'
import React, { Component } from 'react'
import styled from 'styled-components'
import Button from '../components/Button'
import Card from '../components/Card'
import Prompts from '../components/Prompts'
import Row from '../components/Row'
import Slider from '../components/Slider'
import Switcher from '../components/Switcher'

// TODO: Make sure that the prompts embedded here only display for counselors, and only when not in
//       a third session.

const ImageWrap = styled.div`
  position: relative;
  display: ${props => props.shown ? 'block' : 'none'};
`

const Image = styled.img`
  width: 100%;
  position: ${props => props.zIndex === '0' ? 'relative' : 'absolute'};
  z-index: ${props => props.zIndex};
  top: 0;
  left: 0;
  opacity: ${props => props.shown ? '1' : '0'};
  transition: ${props => props.animate ? 'opacity 2s' : 'none'};
`

const PlayingSpinner = styled.i`
  position: absolute;
  bottom: 1rem;
  right: 1rem;
  font-size: 1.2rem;
  color: #fff;
  z-index: 10;
`

@observer
export default class VisualFieldProgression extends Component {
  @observable scene = 'DRIVING'
  @observable step = 0
  @observable playing = false
  @observable disabled = false
  @observable animate = false

  @computed
  get yearsWithoutTreatment() {
    return (
      (this.step === 4 & 10) ||
      (this.step === 3 && 5) ||
      (this.step === 2 && 3) ||
      (this.step === 1 && 1) ||
      0
    )
  }

  @action
  setScene = scene => {
    this.scene = scene
  }

  @action
  setStep = step => {
    this.step = step
  }

  @action
  startPlaying = () => {
    this.animate = true
    this.disabled = true
    this.playing = true
  }

  @action
  stopPlaying = () => {
    this.animate = false
    this.disabled = false
    this.playing = false
  }

  @action
  progress = () => {
    this.startPlaying()
    this.setStep(this.step === 4 ? 0 : this.step + 1)
    setTimeout(this.stopPlaying, 2000)
  }

  @action
  stopWhatsMissing = () => {
    this.disabled = false
  }

  @action
  progressWhatsMissing = (currentStep, iterations) => {   
    if (iterations >= 5 && this.step === 0) {
      this.setStep(currentStep)
      this.disabled = false
    } else {
      this.setStep(this.step === 0 ? currentStep : 0)
      setTimeout(() => this.progressWhatsMissing(currentStep, iterations + 1), 1500)
    }
  }

  @action
  startWhatsMissing = () => {
    this.disabled = true
    this.progressWhatsMissing(this.step, 0)
  }

  render() {
    const { screening, isCombat } = this.props
    const drivingImagePath = isCombat? 'combat' : 'driving'
    return (
      <Card>
        {!isCombat && <Card.Pad>
          <Switcher
            color="blue"
            choices="vfpScene"
            value={this.scene}
            onChange={this.setScene}
          />
        </Card.Pad> }
        <Card.Pad>
          <Row alignCenter>
            <Row.Cell>
              <Card.Header str="titles.yearsWithoutTreatment" />
            </Row.Cell>
            <Row.Cell flex="3">
              <Slider
                disabled={this.disabled}
                min={0}
                max={4}
                value={this.step}
                labels={['0', '1', '3', '5', '10']}
                pinColor="blue"
                onChange={this.setStep}
              />
            </Row.Cell>
          </Row>
        </Card.Pad>
        <Card.Divider />
        <Card.Pad>
          {this.scene === 'DRIVING' && this.step === 1 && !this.disabled &&
            <Prompts>
              <Prompts.Q str="prose.didYouNoticeMostPeopleDont" small />
              <Prompts.P str="prose.itWasAPedestrian" small />
            </Prompts>
          }
          {this.scene === 'DRIVING' && this.step === 2 && !this.disabled &&
            <Prompts>
              <Prompts.Q str="prose.didYouNotice" small />
              <Prompts.P str="prose.itWasASemiTruck" small />
            </Prompts>
          }
          {this.scene === 'KITCHEN' && this.step === 1 && !this.disabled &&
            <Prompts>
              <Prompts.Q str="prose.didYouNoticePeopleInCrowd" small />
            </Prompts>
          }
          {this.scene === 'KITCHEN' && this.step === 2 && !this.disabled &&
            <Prompts>
              <Prompts.Q str="prose.didYouNotice" small />
              <Prompts.P str="prose.itWasSomeIngredients" small />
            </Prompts>
          }
          {this.scene === 'RELAXING' && this.step === 1 && !this.disabled &&
            <Prompts>
              <Prompts.Q str="prose.didYouNoticeThingsAroundHouse" small />
            </Prompts>
          }
          {this.scene === 'RELAXING' && this.step === 2 && !this.disabled &&
            <Prompts>
              <Prompts.Q str="prose.didYouNotice" small />
              <Prompts.P str="prose.itWasSomethingOnTheFloor" small />
            </Prompts>
          }
          {this.step === 3 && !this.disabled &&
            <Prompts>
              <Prompts.P str="prose.itTookUntilFiveYears" small />
            </Prompts>
          }
          {this.step === 4 && !this.disabled &&
            <Prompts>
              <Prompts.P str="prose.itTookUntilTenYears" small />
            </Prompts>
          }
          <Row alignCenter>
            <Row.Cell flex="3">
              <ImageWrap shown={this.scene === 'DRIVING'}>
                <Image animate={this.animate} alt="" src={`/images/visual-field-progression/${drivingImagePath}/1.jpg`} zIndex="0" shown={true} />
                <Image animate={this.animate} alt="" src={`/images/visual-field-progression/${drivingImagePath}/2.jpg`} zIndex="1" shown={this.step >= 1} />
                <Image animate={this.animate} alt="" src={`/images/visual-field-progression/${drivingImagePath}/3.jpg`} zIndex="2" shown={this.step >= 2} />
                <Image animate={this.animate} alt="" src={`/images/visual-field-progression/${drivingImagePath}/4.jpg`} zIndex="3" shown={this.step >= 3} />
                <Image animate={this.animate} alt="" src={`/images/visual-field-progression/${drivingImagePath}/5.jpg`} zIndex="4" shown={this.step >= 4} />
                {this.playing && <PlayingSpinner className="fas fa-sync fa-spin" />}
              </ImageWrap>
              <ImageWrap shown={this.scene === 'KITCHEN'}>
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/kitchen/1.jpg" zIndex="0" shown={true} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/kitchen/2.jpg" zIndex="1" shown={this.step >= 1} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/kitchen/3.jpg" zIndex="2" shown={this.step >= 2} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/kitchen/4.jpg" zIndex="3" shown={this.step >= 3} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/kitchen/5.jpg" zIndex="4" shown={this.step >= 4} />
                {this.playing && <PlayingSpinner className="fas fa-sync fa-spin" />}
              </ImageWrap>
              <ImageWrap shown={this.scene === 'RELAXING'}>
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/relaxing/1.jpg" zIndex="0" shown={true} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/relaxing/2.jpg" zIndex="1" shown={this.step >= 1} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/relaxing/3.jpg" zIndex="2" shown={this.step >= 2} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/relaxing/4.jpg" zIndex="3" shown={this.step >= 3} />
                <Image animate={this.animate} alt="" src="/images/visual-field-progression/relaxing/5.jpg" zIndex="4" shown={this.step >= 4} />
                {this.playing && <PlayingSpinner className="fas fa-sync fa-spin" />}
              </ImageWrap>
            </Row.Cell>
            {!screening &&
              <Row.Cell>
                <ImageWrap shown={true}>
                  <Image animate={this.animate} alt="" src="/images/visual-field-progression/fields/1.jpg" zIndex="0" shown={true} />
                  <Image animate={this.animate} alt="" src="/images/visual-field-progression/fields/2.jpg" zIndex="1" shown={this.step >= 1} />
                  <Image animate={this.animate} alt="" src="/images/visual-field-progression/fields/3.jpg" zIndex="2" shown={this.step >= 2} />
                  <Image animate={this.animate} alt="" src="/images/visual-field-progression/fields/4.jpg" zIndex="3" shown={this.step >= 3} />
                  <Image animate={this.animate} alt="" src="/images/visual-field-progression/fields/5.jpg" zIndex="4" shown={this.step >= 4} />
                </ImageWrap>
              </Row.Cell>
            }
          </Row>
          <Row>
            <Row.Cell>
              <Button
                color="blue"
                str={this.step === 4 && !this.disabled ? 'titles.reset' : 'titles.play'}
                disabled={this.disabled}
                onClick={this.progress}
              />
            </Row.Cell>
            <Row.Cell>
              <Button
                color="blue"
                str="titles.whatsMissing"
                disabled={this.disabled || this.step === 0}
                onClick={this.startWhatsMissing}
              />
            </Row.Cell>
          </Row>
        </Card.Pad>
      </Card>
    )
  }
}



// WEBPACK FOOTER //
// ./src/components/VisualFieldProgression.js